import { useState } from "react";
import {
  loginUser,
  registerUser,
  loginWithGoogle,
  resetPassword,
} from "../../services/firebase";
import { trackUserSession } from "../../services/analytics";

/**
 * @param {{ onClose: ()=>void }} props
 */
export default function AuthModal({ onClose }) {
  const [mode, setMode] = useState("login"); // "login" | "register" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function handleSubmit(e) {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "reset") {
        await resetPassword(email);
        setResetSent(true);
      } else if (mode === "login") {
        const cred = await loginUser(email, password);
        trackUserSession(cred?.user?.uid, email);
        onClose();
      } else {
        const cred = await registerUser(email, password, name);
        trackUserSession(cred?.user?.uid, email);
        onClose();
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleAuth() {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      onClose();
    } catch (err) {
      // Handle case where user closes the popup without signing in
      if (err.code !== "auth/popup-closed-by-user") {
        setError(friendlyError(err.code));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Card */}
      <div className="animate-slideUp w-full max-w-sm mx-4 bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-brand-border">
          <h2 className="font-serif text-xl text-brand-text">
            {mode === "login"
              ? "Welcome back"
              : mode === "register"
                ? "Create account"
                : "Reset password"}
          </h2>
          <p className="text-xs text-brand-dim font-sans mt-1">
            {mode === "login"
              ? "Sign in to log your meals and track progress."
              : mode === "register"
                ? "Takes 10 seconds — no email verification needed."
                : "Enter your email and we'll send a reset link."}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-3">
          {mode === "register" && (
            <Input
              label="Display name"
              type="text"
              value={name}
              onChange={setName}
              placeholder="Youssef"
            />
          )}
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
          />
          {mode !== "reset" && (
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
            />
          )}

          {mode === "login" && (
            <button
              type="button"
              onClick={() => { setMode("reset"); setError(""); setResetSent(false); }}
              className="self-end text-[10px] text-brand-green hover:underline font-sans -mt-1"
            >
              Forgot password?
            </button>
          )}

          {resetSent && mode === "reset" && (
            <p className="text-xs text-brand-green font-sans bg-brand-green/10 px-3 py-2 rounded border border-brand-green/20">
              Reset link sent! Check your inbox (and spam folder).
            </p>
          )}

          {error && (
            <p className="text-xs text-brand-red font-sans bg-brand-red/10 px-3 py-2 rounded border border-brand-red/20">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-1 w-full py-2.5 rounded bg-brand-green text-brand-bg font-sans font-bold
                       text-xs uppercase tracking-widest hover:bg-green-400 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Please wait…"
              : mode === "login"
                ? "Sign in →"
                : mode === "register"
                  ? "Create account →"
                  : "Send reset link →"}
          </button>

          {/* --- Divider --- */}
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-brand-border"></span>
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-sans">
              <span className="bg-brand-surface px-2 text-brand-dim">
                Or continue with
              </span>
            </div>
          </div>

          {/* --- Google Button --- */}
          <button
            type="button"
            onClick={handleGoogleAuth}
            disabled={loading}
            className="w-full py-2.5 rounded border border-brand-border bg-transparent text-brand-text 
                       font-sans text-xs flex items-center justify-center gap-2 hover:bg-brand-border/30 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </button>

          <p className="text-center text-[10px] text-brand-dim font-sans mt-2">
            {mode === "reset" ? (
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); setResetSent(false); }}
                className="text-brand-green hover:underline"
              >
                ← Back to sign in
              </button>
            ) : (
              <>
                {mode === "login" ? "No account?" : "Already registered?"}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === "login" ? "register" : "login");
                    setError("");
                  }}
                  className="text-brand-green hover:underline"
                >
                  {mode === "login" ? "Register" : "Sign in"}
                </button>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}

/* ── tiny local input component ── */
function Input({ label, type, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-brand-dim font-sans mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="w-full bg-brand-bg border border-brand-border rounded px-3 py-2
                    text-base sm:text-sm text-brand-text font-sans placeholder-brand-dim/40
                   focus:border-brand-green/50 transition-colors outline-none"
      />
    </div>
  );
}

/** Map Firebase error codes to friendly messages. */
function friendlyError(code) {
  const map = {
    "auth/invalid-email": "Invalid email address.",
    "auth/user-not-found": "No account with that email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/email-already-in-use": "That email is already registered.",
    "auth/weak-password": "Password must be at least 6 characters.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/too-many-requests": "Too many attempts. Try again later.",
    "auth/operation-not-allowed": "Google login is currently disabled.",
  };
  return map[code] || "Something went wrong. Please try again.";
}
